import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridColumnVisibilityModel,
  GridRowGroupingModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useLinkedServiceSelection } from 'src/v3/manage/hooks/use-linked-service-selection'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { InfoIcon } from 'src/v3/core/icons'
import {
  activeLinkedServiceChanged,
  assignmentDateFiltersChanged,
  assignmentAnalyticsFiltersChanged,
  assignmentAnalyticsReset,
  fetchAssignmentAnalytics,
  fetchAssignmentAnalyticsMetrics,
  fetchLinkedServices,
  selectAssignmentAnalytics,
  selectAssignmentDateFilters,
  selectAssignmentAnalyticsFilters,
  selectAssignmentAnalyticsMetrics,
  selectAssignmentAnalyticsMetricsStatus,
  selectAssignmentAnalyticsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from '../manage.slice'
import {
  CREDIT_SOURCE_CONFIG,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../constants'
import { dataGridSx } from '../styles'
import ManualCreditsInfoDialog from '../components/manual-credits-info-dialog'
import SummaryMetrics from './components/summary-metrics'
import {
  AnalyticsRow,
  AssignmentHealthStatus,
  CourseCompletionStatus,
  CreditSource,
  GroupingView,
} from './types'

let groupingModels: Record<GroupingView, GridRowGroupingModel> = {
  assignment: ['assignmentName', 'courseName'],
  user: ['userName', 'assignmentName'],
  course: ['courseName', 'assignmentName'],
}

let healthStatusConfig: Record<
  AssignmentHealthStatus,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }
> = {
  active: { label: 'Active', color: 'info' },
  at_risk: { label: 'At Risk', color: 'warning' },
  overdue: { label: 'Overdue', color: 'error' },
  completed: { label: 'Completed', color: 'success' },
}

let courseStatusConfig: Record<
  CourseCompletionStatus,
  { label: string; color: 'success' | 'error' | 'warning' }
> = {
  completed: { label: 'Completed', color: 'success' },
  incomplete: { label: 'Incomplete', color: 'error' },
  partial: { label: 'In Progress', color: 'warning' },
}

let statusFilterOptions: Array<{
  value: AssignmentHealthStatus | 'all'
  label: string
  color: 'default' | 'info' | 'warning' | 'error' | 'success'
}> = [
  { value: 'all', label: 'All', color: 'default' },
  { value: 'active', label: 'Active', color: 'info' },
  { value: 'at_risk', label: 'At Risk', color: 'warning' },
  { value: 'overdue', label: 'Overdue', color: 'error' },
  { value: 'completed', label: 'Completed', color: 'success' },
]

let statusSeverity: Record<AssignmentHealthStatus, number> = {
  overdue: 3,
  at_risk: 2,
  active: 1,
  completed: 0,
}

// Aggregation: worst (most severe) assignment status in the group
let worstStatusAggregation: GridAggregationFunction<
  AssignmentHealthStatus | null,
  AssignmentHealthStatus | null
> = {
  apply: ({ values }) =>
    (
      values.filter(Boolean) as AssignmentHealthStatus[]
    ).reduce<AssignmentHealthStatus | null>(
      (worst, s) =>
        worst === null || statusSeverity[s] > statusSeverity[worst] ? s : worst,
      null,
    ),
  label: '',
  hasCellUnit: false,
}

// Aggregation: show the first non-null value in the group row (e.g. due date is
// the same across all rows in an assignment group)
let firstValueAggregation: GridAggregationFunction<
  string | null,
  string | null
> = {
  apply: ({ values }) => values.find((v) => v != null) ?? null,
  label: '',
  hasCellUnit: false,
}

// Aggregation: show the most recent date in the group row (ISO strings compare lexically)
let latestDateAggregation: GridAggregationFunction<
  string | null,
  string | null
> = {
  apply: ({ values }) =>
    (values.filter(Boolean) as string[]).reduce<string | null>(
      (max, d) => (max === null || d > max ? d : max),
      null,
    ),
  label: '',
  hasCellUnit: false,
}

let analyticsAggregationFunctions = {
  firstValue: firstValueAggregation,
  latestDate: latestDateAggregation,
  worstStatus: worstStatusAggregation,
}

let analyticsAggregationModel = {
  assignmentDueDate: 'firstValue',
  lastActivityDate: 'latestDate',
  completionPercent: 'avg',
  assignmentStatus: 'worstStatus',
  userOverallCompletionPercent: 'firstValue',
  assignmentCreditSource: 'firstValue',
}

let completionTooltips: Record<GroupingView, string> = {
  assignment:
    'Group rows: equal-weight average completion across all enrolled users — each user counted once.\n\nSub-group rows: average completion for the subset of users grouped under that course.',
  user: "Group rows: equal-weight average across all of this user's assignments — each assignment counted once, regardless of how many courses it contains.\n\nSub-group rows: computed completion rate for that assignment (courses completed ÷ total courses).",
  course:
    'Group rows: equal-weight average completion across all users enrolled in assignments containing this course.\n\nSub-group rows: average completion rate for users enrolled in that specific assignment.',
}

export default function AssignmentAnalytics() {
  let [creditInfoOpen, setCreditInfoOpen] = useState(false)
  let dispatch = useAppDispatch()
  let linkedServiceID = useLinkedServiceSelection()
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let dateFilters = useAppSelector(selectAssignmentDateFilters)
  let filters = useAppSelector(selectAssignmentAnalyticsFilters)
  let rows = useAppSelector(selectAssignmentAnalytics(linkedServiceID))
  let analyticsStatus = useAppSelector(
    selectAssignmentAnalyticsStatus(linkedServiceID),
  )
  let metrics = useAppSelector(
    selectAssignmentAnalyticsMetrics(linkedServiceID),
  )
  let metricsStatus = useAppSelector(
    selectAssignmentAnalyticsMetricsStatus(linkedServiceID),
  )

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (!linkedServiceID || linkedServiceID === 'unselected') return
    if (analyticsStatus === 'uninitialized') {
      dispatch(
        fetchAssignmentAnalytics({
          linkedServiceID,
          startDate: dateFilters.startDate ?? undefined,
          endDate: dateFilters.endDate ?? undefined,
        }),
      )
      dispatch(
        fetchAssignmentAnalyticsMetrics({
          linkedServiceID,
          startDate: dateFilters.startDate ?? undefined,
          endDate: dateFilters.endDate ?? undefined,
        }),
      )
    }
  }, [linkedServiceID, analyticsStatus, dispatch])

  let isLinkedServicesLoaded =
    linkedServicesStatus === 'fulfilled' || linkedServicesStatus === 'rejected'
  let loading =
    analyticsStatus === 'uninitialized' || analyticsStatus === 'pending'
  let metricsLoading =
    metricsStatus === 'uninitialized' || metricsStatus === 'pending'

  // Count unique assignments per status for chip labels
  let statusCounts = useMemo(() => {
    let uniqueFor = (status: AssignmentHealthStatus) =>
      new Set(
        rows
          .filter((r) => r.assignmentStatus === status)
          .map((r) => r.assignmentID),
      ).size
    return {
      all: new Set(rows.map((r) => r.assignmentID)).size,
      active: uniqueFor('active'),
      at_risk: uniqueFor('at_risk'),
      overdue: uniqueFor('overdue'),
      completed: uniqueFor('completed'),
    }
  }, [rows])

  let filteredRows = useMemo(
    () =>
      filters.statusFilter === 'all'
        ? rows
        : rows.filter((r) => r.assignmentStatus === filters.statusFilter),
    [rows, filters.statusFilter],
  )

  let apiRef = useGridApiRef()

  let allColumns = useMemo<GridColDef<AnalyticsRow>[]>(
    () => [
      {
        field: 'assignmentName',
        headerName: 'Assignment',
        flex: 2,
        minWidth: 220,
      },
      {
        field: 'userName',
        headerName: 'User',
        flex: 1.5,
        minWidth: 160,
      },
      {
        field: 'userEmail',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: 'courseName',
        headerName: 'Course',
        flex: 2,
        minWidth: 220,
      },
      // Hidden column — supplies the API-computed unweighted avg for "By User" top rows
      {
        field: 'userOverallCompletionPercent',
        headerName: 'User Overall Completion',
        width: 0,
        type: 'number',
        aggregable: true,
      },
      {
        field: 'completionPercent',
        headerName: 'Completion',
        width: 160,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        aggregable: true,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
              }}
            >
              Completion
            </Typography>
            <Tooltip
              title={
                <Box sx={{ whiteSpace: 'pre-line' }}>
                  {completionTooltips[filters.groupingView]}
                </Box>
              }
              placement="top"
              arrow
            >
              <InfoIcon
                sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        ),
        renderCell: (params) => {
          let { rowNode, id } = params
          // Leaf rows: the courseStatus chip already conveys per-course status
          if (rowNode.type === 'leaf') return null
          if (rowNode.type !== 'group') return null
          // params.value is null for group rows — aggregated values must be read via getCellValue.
          // "By User" top rows: use API-computed unweighted avg to avoid course-count bias.
          // All other group tiers: avg aggregation is ironclad (uniform course counts per assignment).
          let field =
            rowNode.groupingField === 'userName'
              ? 'userOverallCompletionPercent'
              : 'completionPercent'
          let cellValue = apiRef.current?.getCellValue(id, field)
          let pct = cellValue == null ? 0 : Math.round(cellValue as number)
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '100%',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={pct}
                color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                sx={{ flex: 1, height: 6, borderRadius: 1 }}
              />
              <Typography
                variant="caption"
                sx={{ minWidth: 32, textAlign: 'right' }}
              >
                {pct}%
              </Typography>
            </Box>
          )
        },
      },
      {
        field: 'courseStatus',
        headerName: 'Course Status',
        width: 140,
        renderCell: ({ value }) => {
          if (!value) return null
          let config = courseStatusConfig[value as CourseCompletionStatus]
          return (
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              variant="outlined"
            />
          )
        },
      },
      {
        field: 'courseCreditSource',
        headerName: 'Course Credit Source',
        width: 150,
        renderCell: ({ value, rowNode }) => {
          if (rowNode.type !== 'leaf' || !value) return null
          let config = CREDIT_SOURCE_CONFIG[value as CreditSource]
          return (
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              variant="outlined"
            />
          )
        },
      },
      {
        field: 'assignmentCreditSource',
        headerName: 'Assignment Credit Source',
        width: 160,
        aggregable: true,
        renderCell: ({ value, rowNode }) => {
          if (rowNode.type === 'leaf' || !value) return null
          let config = CREDIT_SOURCE_CONFIG[value as CreditSource]
          return (
            <Chip
              label={config.label}
              color={config.color}
              size="small"
              variant="outlined"
            />
          )
        },
      },
      {
        field: 'assignmentStatus',
        headerName: 'Assignment Status',
        width: 140,
        aggregable: true,
        renderCell: ({ value }) => {
          if (!value) return null
          let config = healthStatusConfig[value as AssignmentHealthStatus]
          return <Chip label={config.label} color={config.color} size="small" />
        },
      },
      {
        field: 'assignmentDueDate',
        headerName: 'Due Date',
        width: 140,
        aggregable: true,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).format('MMM D, YYYY') : null,
      },
      {
        field: 'lastActivityDate',
        headerName: 'Last Activity',
        width: 140,
        aggregable: true,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).format('MMM D, YYYY') : null,
      },
    ],
    [apiRef, filters.groupingView],
  )

  let groupedFields = groupingModels[filters.groupingView]

  let columnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      ...filters.columnVisibility,
      ...Object.fromEntries(groupedFields.map((f) => [f, false])),
      userOverallCompletionPercent: false,
    }),
    [filters.columnVisibility, groupedFields],
  )

  let handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      let userPrefs = Object.fromEntries(
        Object.entries(model).filter(([f]) => !groupedFields.includes(f)),
      )
      dispatch(
        assignmentAnalyticsFiltersChanged({ columnVisibility: userPrefs }),
      )
    },
    [groupedFields, dispatch],
  )

  let MainToolbar = useCallback(
    () => <DataGridToolbar fileName="assignment-analytics" />,
    [],
  )

  let handleViewChange = useCallback(
    (_: unknown, value: GroupingView | null) => {
      if (value)
        dispatch(assignmentAnalyticsFiltersChanged({ groupingView: value }))
    },
    [dispatch],
  )

  let resetForService = useCallback(() => {
    if (linkedServiceID && linkedServiceID !== 'unselected') {
      dispatch(assignmentAnalyticsReset(linkedServiceID))
    }
  }, [dispatch, linkedServiceID])

  return (
    <Container maxWidth="xl" className="py-6">
      <Typography variant="h4" component="h1" className="mb-6">
        Assignment Analytics
      </Typography>
      <Box sx={{ mb: 3 }}>
        <SummaryMetrics loading={metricsLoading} metrics={metrics} />
      </Box>
      {isLinkedServicesLoaded ? (
        <Card className="p-6 mb-6" sx={{ bgcolor: 'background.paper' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
              }}
            >
              <FormControl
                sx={{ minWidth: 180, backgroundColor: 'background.paper' }}
              >
                <InputLabel id="linked-service-filter">
                  Linked Service
                </InputLabel>
                <Select
                  labelId="linked-service-filter"
                  label="Linked Service"
                  value={linkedServiceID || ''}
                  onChange={(event) =>
                    dispatch(activeLinkedServiceChanged(event.target.value))
                  }
                  size="medium"
                  displayEmpty
                >
                  {linkedServices.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="From"
                value={
                  dateFilters.startDate ? dayjs(dateFilters.startDate) : null
                }
                onChange={(value) => {
                  dispatch(
                    assignmentDateFiltersChanged({
                      startDate: value?.toISOString() ?? null,
                    }),
                  )
                  resetForService()
                }}
                slotProps={{
                  textField: {
                    size: 'medium',
                    sx: { bgcolor: 'background.paper' },
                  },
                }}
              />

              <DatePicker
                label="To"
                value={dateFilters.endDate ? dayjs(dateFilters.endDate) : null}
                onChange={(value) => {
                  dispatch(
                    assignmentDateFiltersChanged({
                      endDate: value?.toISOString() ?? null,
                    }),
                  )
                  resetForService()
                }}
                slotProps={{
                  textField: {
                    size: 'medium',
                    sx: { bgcolor: 'background.paper' },
                  },
                }}
              />

              <Box
                sx={{
                  gridColumn: { lg: 'span 3' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Group by:
                </Typography>
                <ToggleButtonGroup
                  value={filters.groupingView}
                  exclusive
                  onChange={handleViewChange}
                  size="small"
                >
                  <ToggleButton value="assignment">By Assignment</ToggleButton>
                  <ToggleButton value="user">By User</ToggleButton>
                  <ToggleButton value="course">By Course</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </LocalizationProvider>
        </Card>
      ) : (
        <Card
          className="p-6 mb-6"
          sx={{ bgcolor: 'background.paper' }}
          role="status"
          aria-busy="true"
          aria-label="Loading filters"
        >
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
            }}
            aria-hidden="true"
          >
            <Skeleton
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: 1, gridColumn: { lg: 'span 3' } }}
            />
          </Box>
        </Card>
      )}
      <Card className="p-6 mb-6">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {statusFilterOptions.map(({ value, label, color }) => (
              <Chip
                key={value}
                label={`${label} (${statusCounts[value]})`}
                color={color}
                variant={filters.statusFilter === value ? 'filled' : 'outlined'}
                onClick={() =>
                  dispatch(
                    assignmentAnalyticsFiltersChanged({ statusFilter: value }),
                  )
                }
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<InfoIcon sx={{ fontSize: 16 }} />}
            onClick={() => setCreditInfoOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            About manual credits
          </Button>
        </Box>

        {loading ? (
          <Skeleton
            variant="rectangular"
            height={480}
            sx={{ borderRadius: 2 }}
          />
        ) : (
          <DataGridPremium
            apiRef={apiRef}
            rows={filteredRows}
            columns={allColumns}
            rowGroupingModel={groupedFields}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            defaultGroupingExpansionDepth={1}
            groupingColDef={{
              minWidth: 280,
              flex: 2,
              headerName: {
                assignment: 'Assignment',
                user: 'User',
                course: 'Course',
              }[filters.groupingView],
            }}
            aggregationFunctions={{
              ...GRID_AGGREGATION_FUNCTIONS,
              ...analyticsAggregationFunctions,
              avg: { ...GRID_AGGREGATION_FUNCTIONS.avg, label: '' },
            }}
            aggregationModel={analyticsAggregationModel}
            getAggregationPosition={() => 'inline'}
            initialState={{
              pagination: {
                paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
              },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            pagination
            disableRowSelectionOnClick
            showToolbar
            slots={{ toolbar: MainToolbar }}
            sx={dataGridSx}
          />
        )}
      </Card>
      <ManualCreditsInfoDialog
        open={creditInfoOpen}
        onClose={() => setCreditInfoOpen(false)}
      />
    </Container>
  )
}
